// Central content source for NeuroTriq TechLabs.
// Icon fields store lucide-react icon names (resolved via <Icon/> helper).

export const company = {
  name: "NeuroTriq TechLabs",
  short: "NeuroTriq",
  tagline: "Empowering Businesses Through Smart Technology",
  altTagline: "Building Reliable Digital Solutions for Growing Businesses",
  description:
    "NeuroTriq TechLabs is an IT services and technology consulting firm helping enterprises, SMBs and startups design, build and scale reliable digital products — from custom software and cloud platforms to AI, data and security.",
  email: "pushpa.bharti@neurotriq.com",
  salesEmail: "pushpa.bharti@neurotriq.com",
  phone: "+91 78274 32485",
  phoneHref: "+917827432485",
  whatsapp: "917827432485",
  whatsappMsg: "Hello NeuroTriq TechLabs, I'm interested in your IT services. Please contact me.",
  address: "New Delhi, India",
  hoursList: [
    { day: "Monday – Friday", time: "9:00 AM – 6:00 PM IST" },
    { day: "Saturday", time: "10:00 AM – 2:00 PM IST" },
    { day: "Sunday", time: "Closed" },
  ],
  hours: "Mon–Fri 9AM–6PM · Sat 10AM–2PM · Sun Closed (IST)",
  founded: "2015",
  logo: "/logo.jpg",
  social: {
    linkedin: "https://linkedin.com",
    github: "https://github.com",
    twitter: "https://twitter.com",
    dribbble: "https://dribbble.com",
  },
};

export const nav = [
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "Technologies", to: "/technologies" },
  { label: "Portfolio", to: "/portfolio" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "Careers", to: "/careers" },
  { label: "Blog", to: "/blog" },
];

export const stats = [
  { value: 50, suffix: "+", label: "Projects Delivered" },
  { value: 98, suffix: "%", label: "Client Satisfaction" },
  { value: 24, suffix: "×7", label: "Support & Monitoring" },
  { value: 10, suffix: "+", label: "Core Technologies" },
];

export const whyChooseUs = [
  { icon: "Users", title: "Experienced Team", text: "Senior engineers, architects and designers with a proven enterprise delivery track record." },
  { icon: "BadgeDollarSign", title: "Transparent Pricing", text: "Clear, milestone-based estimates with no hidden costs — you always know where budget goes." },
  { icon: "GitBranch", title: "Transparent Process", text: "Shared boards, weekly demos and open communication from discovery to deployment." },
  { icon: "Rocket", title: "Fast Delivery", text: "Agile sprints and reusable accelerators help us ship production value in weeks, not months." },
  { icon: "ShieldCheck", title: "Enterprise Security", text: "Security-first engineering, VAPT and compliance baked into every stage of the lifecycle." },
  { icon: "Headset", title: "Dedicated Support", text: "24×7 monitoring, SLAs and a named delivery lead who owns your outcomes." },
  { icon: "Lightbulb", title: "Innovation Driven", text: "We bring modern patterns — AI, cloud-native and automation — to real business problems." },
  { icon: "Scaling", title: "Scalable Solutions", text: "Architectures designed to grow with you, from first launch to millions of users." },
];

export const services = [
  {
    slug: "software-development",
    icon: "Code2",
    title: "Software Development",
    tagline: "Custom platforms engineered around your business.",
    description:
      "Bespoke software, ERP, CRM and line-of-business applications built for reliability, security and scale — web, desktop and cloud-native.",
    features: ["Custom Software", "ERP Platforms", "CRM Systems", "Business Applications", "Desktop Apps", "Cloud-Native Apps"],
    image: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=1200&q=80",
  },
  {
    slug: "website-development",
    icon: "Globe",
    title: "Website Development",
    tagline: "Sites that convert and scale.",
    description:
      "High-performance corporate sites, e-commerce and marketing experiences using modern frameworks and headless CMS.",
    features: ["Corporate Websites", "E-Commerce", "Landing Pages", "Headless CMS", "WordPress", "Next.js"],
    image: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?auto=format&fit=crop&w=1200&q=80",
  },
  {
    slug: "mobile-app-development",
    icon: "Smartphone",
    title: "Mobile App Development",
    tagline: "Native-grade apps, everywhere.",
    description:
      "Beautiful, performant apps for iOS and Android — native or cross-platform — with enterprise integrations and offline support.",
    features: ["Android", "iOS", "Flutter", "React Native", "Enterprise Apps", "App Modernization"],
    image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1200&q=80",
  },
  {
    slug: "cloud-services",
    icon: "Cloud",
    title: "Cloud Services",
    tagline: "Cloud that's fast, secure and cost-aware.",
    description:
      "Migration, architecture and optimization across AWS, Azure and GCP — with security and FinOps built in.",
    features: ["AWS", "Azure", "Google Cloud", "Cloud Migration", "Cost Optimization", "Cloud Security"],
    image: "https://images.pexels.com/photos/17489163/pexels-photo-17489163.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    slug: "devops",
    icon: "Infinity",
    title: "DevOps",
    tagline: "Ship faster, safer, automatically.",
    description:
      "CI/CD, containers and infrastructure-as-code that turn releases into a non-event — with full observability.",
    features: ["CI/CD", "Docker", "Kubernetes", "Terraform", "Monitoring", "Infrastructure as Code"],
    image: "https://images.unsplash.com/photo-1607799279861-4dd421887fb3?auto=format&fit=crop&w=1200&q=80",
  },
  {
    slug: "ai-solutions",
    icon: "BrainCircuit",
    title: "AI Solutions",
    tagline: "Generative AI that ships to production.",
    description:
      "From RAG assistants to intelligent automation — LLM integrations with OpenAI, Claude and Gemini, engineered responsibly.",
    features: ["Generative AI", "Chatbots & Assistants", "RAG Pipelines", "Automation", "LLM Integration", "OpenAI · Claude · Gemini"],
    image: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=1200&q=80",
  },
  {
    slug: "data-engineering",
    icon: "Database",
    title: "Data Engineering",
    tagline: "Turn raw data into decisions.",
    description:
      "Modern data warehouses, ETL pipelines and analytics that make your data trustworthy and actionable.",
    features: ["Data Warehouses", "Snowflake", "Databricks", "Azure Data Factory", "ETL Pipelines", "Power BI"],
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80",
  },
  {
    slug: "cyber-security",
    icon: "ShieldCheck",
    title: "Cyber Security",
    tagline: "Protect what you've built.",
    description:
      "Security assessments, penetration testing and compliance programs that reduce risk and satisfy auditors.",
    features: ["Security Assessment", "Penetration Testing", "VAPT", "Compliance", "Identity Management", "Threat Monitoring"],
    image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=1200&q=80",
  },
  {
    slug: "managed-it-services",
    icon: "Server",
    title: "Managed IT Services",
    tagline: "Your infrastructure, always on.",
    description:
      "Proactive infrastructure management, 24×7 monitoring and helpdesk so your teams stay productive.",
    features: ["Infrastructure Management", "24×7 Monitoring", "Remote Support", "IT Helpdesk", "AMC", "Server Management"],
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1200&q=80",
  },
];

export const industries = [
  { icon: "HeartPulse", name: "Healthcare", text: "HIPAA-ready portals, telehealth and hospital systems." },
  { icon: "Landmark", name: "Finance", text: "Secure fintech, payments and core banking modernization." },
  { icon: "ShoppingBag", name: "Retail", text: "Omnichannel commerce and inventory intelligence." },
  { icon: "Factory", name: "Manufacturing", text: "IoT, MES and supply-chain visibility platforms." },
  { icon: "GraduationCap", name: "Education", text: "LMS, student portals and adaptive learning tools." },
  { icon: "Building2", name: "Government", text: "Citizen services and compliant public-sector systems." },
  { icon: "RadioTower", name: "Telecom", text: "OSS/BSS, self-care apps and network automation." },
  { icon: "Building", name: "Real Estate", text: "Property platforms, CRM and virtual experiences." },
  { icon: "Truck", name: "Logistics", text: "Fleet, routing and warehouse management systems." },
  { icon: "Plane", name: "Travel", text: "Booking engines and traveler experience platforms." },
  { icon: "Hotel", name: "Hospitality", text: "PMS, guest apps and revenue optimization." },
  { icon: "Car", name: "Automobile", text: "Connected vehicle, dealer and aftersales solutions." },
  { icon: "Sparkles", name: "Startups", text: "MVPs and scale-ready foundations for founders." },
];

export const technologies = [
  { name: "React", category: "Frontend" },
  { name: "Next.js", category: "Frontend" },
  { name: "Node.js", category: "Backend" },
  { name: "Python", category: "Backend" },
  { name: "Java", category: "Backend" },
  { name: ".NET", category: "Backend" },
  { name: "Spring Boot", category: "Backend" },
  { name: "Flutter", category: "Mobile" },
  { name: "React Native", category: "Mobile" },
  { name: "Docker", category: "DevOps" },
  { name: "Kubernetes", category: "DevOps" },
  { name: "Terraform", category: "DevOps" },
  { name: "AWS", category: "Cloud" },
  { name: "Azure", category: "Cloud" },
  { name: "GCP", category: "Cloud" },
  { name: "MongoDB", category: "Data" },
  { name: "PostgreSQL", category: "Data" },
  { name: "MySQL", category: "Data" },
  { name: "Redis", category: "Data" },
  { name: "Snowflake", category: "Data" },
  { name: "Databricks", category: "Data" },
  { name: "Power BI", category: "Data" },
  { name: "OpenAI", category: "AI" },
  { name: "Claude", category: "AI" },
  { name: "Gemini", category: "AI" },
];

export const techCategories = ["All", "Frontend", "Backend", "Mobile", "Cloud", "DevOps", "Data", "AI"];

export const process = [
  { icon: "Search", step: "01", title: "Discovery", text: "We immerse in your goals, users and constraints to define the right problem." },
  { icon: "ClipboardList", step: "02", title: "Planning", text: "Scope, architecture and a milestone roadmap with clear success metrics." },
  { icon: "PenTool", step: "03", title: "Design", text: "Experience and interface design validated with interactive prototypes." },
  { icon: "Code2", step: "04", title: "Development", text: "Agile sprints, clean code and weekly demos keep you in control." },
  { icon: "TestTube2", step: "05", title: "Testing", text: "Automated and manual QA, security and performance hardening." },
  { icon: "Rocket", step: "06", title: "Deployment", text: "Zero-downtime releases with CI/CD and full observability." },
  { icon: "LifeBuoy", step: "07", title: "Support", text: "24×7 monitoring, SLAs and continuous improvement post-launch." },
];

export const testimonials = [
  {
    quote:
      "NeuroTriq re-platformed our patient portal in under four months. Adoption jumped 60% and support tickets fell dramatically. They feel like an extension of our team.",
    name: "Dr. Alicia Moreau",
    role: "CIO, Meridian Health Group",
    avatar: "https://images.unsplash.com/photo-1594824476967-48c8b964273f?auto=format&fit=crop&w=200&q=80",
  },
  {
    quote:
      "Their cloud migration cut our infrastructure spend by 38% while improving reliability. The FinOps discipline they brought was a game-changer.",
    name: "Marcus Chen",
    role: "VP Engineering, Vaultline Fintech",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&q=80",
  },
  {
    quote:
      "The AI assistant NeuroTriq built now handles 70% of tier-1 queries. It was delivered responsibly, with guardrails our compliance team actually approved.",
    name: "Priya Nair",
    role: "Head of CX, Orbit Retail",
    avatar: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=200&q=80",
  },
  {
    quote:
      "From discovery to launch, everything was transparent. Weekly demos meant zero surprises and a product our customers genuinely love.",
    name: "Daniel Okafor",
    role: "Founder & CEO, Shiftwork",
    avatar: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=200&q=80",
  },
  {
    quote:
      "Their managed services team keeps our manufacturing systems running 24×7. Response times are exceptional and proactive alerts prevent outages.",
    name: "Sofia Rossi",
    role: "COO, Lumen Manufacturing",
    avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=200&q=80",
  },
  {
    quote:
      "We scaled from 5k to 500k users without re-architecting. NeuroTriq designed for growth from day one — exactly what a startup needs.",
    name: "Liam Walsh",
    role: "CTO, Trailhead Travel",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=200&q=80",
  },
];

export const clients = [
  "Meridian Health", "Vaultline", "Orbit Retail", "Lumen", "Trailhead", "Shiftwork",
  "NorthStar", "Cobalt", "Everdyne", "Pulsegrid", "Axonform", "Vertex Labs",
];

export const trustBadges = [
  { icon: "ShieldCheck", label: "Enterprise Security" },
  { icon: "Clock", label: "24×7 Support" },
  { icon: "CloudCog", label: "Cloud Certified" },
  { icon: "BadgeCheck", label: "Quality Assured" },
  { icon: "Scaling", label: "Scalable Solutions" },
  { icon: "Heart", label: "Customer First" },
];

export const values = [
  { icon: "Compass", title: "Outcomes over output", text: "We measure success by your business results, not lines of code." },
  { icon: "ShieldCheck", title: "Security by default", text: "Every decision is made with trust, privacy and resilience in mind." },
  { icon: "Handshake", title: "Radical transparency", text: "Open roadmaps, honest estimates and no black boxes — ever." },
  { icon: "Sparkles", title: "Craft & curiosity", text: "We sweat the details and stay hungry to learn what's next." },
];

export const timeline = [
  { year: "2015", title: "Founded", text: "NeuroTriq TechLabs starts as a three-person engineering studio in New Delhi, India." },
  { year: "2017", title: "Enterprise delivery", text: "First Fortune-scale cloud migration; managed services practice launches." },
  { year: "2019", title: "Global delivery model", text: "Distributed delivery centers open; team crosses 60 engineers." },
  { year: "2021", title: "Data & AI practice", text: "Dedicated data engineering and AI unit formed around modern LLMs." },
  { year: "2023", title: "Security-first", text: "In-house VAPT and compliance practice; SOC-aligned processes adopted." },
  { year: "2025", title: "50+ products live", text: "Trusted by healthcare, fintech, retail and fast-growing startups worldwide." },
];

export const leadership = [
  { name: "Rohan Mehta", role: "Founder & CEO", avatar: "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?auto=format&fit=crop&w=400&q=80" },
  { name: "Elena Petrova", role: "Chief Technology Officer", avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=400&q=80" },
  { name: "James Carter", role: "VP, Delivery", avatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=400&q=80" },
  { name: "Aisha Khan", role: "Head of Design", avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=400&q=80" },
];

export const portfolio = [
  { id: "p1", title: "Meridian Patient Portal", category: "Healthcare", tags: ["Next.js", "AWS", "HIPAA"], image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1000&q=80" },
  { id: "p2", title: "Vaultline Banking Suite", category: "Finance", tags: ["Java", "Kubernetes", "Security"], image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1000&q=80" },
  { id: "p3", title: "Orbit CRM Platform", category: "SaaS", tags: ["React", "Node.js", "PostgreSQL"], image: "https://images.unsplash.com/photo-1552581234-26160f608093?auto=format&fit=crop&w=1000&q=80" },
  { id: "p4", title: "Lumen ERP System", category: "Manufacturing", tags: [".NET", "Azure", "IoT"], image: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&w=1000&q=80" },
  { id: "p5", title: "Shiftwork HRMS", category: "SaaS", tags: ["React", "Python", "GCP"], image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=1000&q=80" },
  { id: "p6", title: "Meridian Hospital Management", category: "Healthcare", tags: ["Angular", "Spring Boot", "MySQL"], image: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=1000&q=80" },
  { id: "p7", title: "Trailhead Restaurant App", category: "Mobile", tags: ["Flutter", "Firebase", "Stripe"], image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1000&q=80" },
  { id: "p8", title: "Cobalt Inventory Software", category: "Retail", tags: ["React", "Node.js", "Redis"], image: "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1000&q=80" },
  { id: "p9", title: "Orbit E-Commerce Platform", category: "Retail", tags: ["Next.js", "Snowflake", "AWS"], image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1000&q=80" },
  { id: "p10", title: "Everdyne AI Chatbot", category: "AI", tags: ["OpenAI", "RAG", "Python"], image: "https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&w=1000&q=80" },
  { id: "p11", title: "Pulsegrid Data Dashboard", category: "Data", tags: ["Databricks", "Power BI", "Airflow"], image: "https://images.unsplash.com/photo-1543286386-713bdd548da4?auto=format&fit=crop&w=1000&q=80" },
  { id: "p12", title: "NorthStar Cloud Migration", category: "Cloud", tags: ["AWS", "Terraform", "Kubernetes"], image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1000&q=80" },
];

export const portfolioCategories = ["All", "Healthcare", "Finance", "Retail", "SaaS", "Manufacturing", "Mobile", "AI", "Data", "Cloud"];

export const caseStudies = [
  {
    id: "cs1",
    client: "Vaultline Fintech",
    title: "Modernizing core banking on the cloud",
    industry: "Finance",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1200&q=80",
    challenge: "A monolithic core banking system caused frequent outages, slow releases and rising infrastructure costs, limiting Vaultline's ability to launch new products.",
    solution: "We decomposed the monolith into secure microservices on Kubernetes, introduced CI/CD and infrastructure-as-code, and implemented zero-trust security and FinOps guardrails.",
    technology: ["Java", "Kubernetes", "Terraform", "AWS", "PostgreSQL"],
    results: [
      { metric: "38%", label: "Lower infra cost" },
      { metric: "99.98%", label: "Platform uptime" },
      { metric: "6×", label: "Faster releases" },
    ],
    impact: "Vaultline now ships new financial products in weeks with confidence, while cutting operating costs and satisfying auditors.",
    roi: "Payback in under 9 months with 3.2× three-year ROI.",
  },
  {
    id: "cs2",
    client: "Meridian Health Group",
    title: "A patient portal patients actually use",
    industry: "Healthcare",
    image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1200&q=80",
    challenge: "Fragmented systems and a dated portal led to low patient engagement and overwhelmed support desks.",
    solution: "We designed and built a HIPAA-ready portal with unified records, telehealth and self-service scheduling, integrated to existing EHR systems.",
    technology: ["Next.js", "Node.js", "AWS", "FHIR"],
    results: [
      { metric: "60%", label: "More adoption" },
      { metric: "45%", label: "Fewer support tickets" },
      { metric: "4.8/5", label: "Patient rating" },
    ],
    impact: "Patients self-serve routine tasks, clinicians save hours weekly, and satisfaction scores reached an all-time high.",
    roi: "Support cost reduced by an estimated $1.2M annually.",
  },
  {
    id: "cs3",
    client: "Orbit Retail",
    title: "AI assistant that deflects 70% of tickets",
    industry: "Retail",
    image: "https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&w=1200&q=80",
    challenge: "Seasonal spikes overwhelmed the support team, driving up costs and hurting response times.",
    solution: "We built a RAG-powered assistant grounded in Orbit's knowledge base with human-handoff, analytics and strict guardrails approved by compliance.",
    technology: ["OpenAI", "Python", "Pinecone", "React"],
    results: [
      { metric: "70%", label: "Tier-1 deflected" },
      { metric: "2.3s", label: "Avg response" },
      { metric: "31%", label: "CSAT uplift" },
    ],
    impact: "The team now focuses on complex cases while customers get instant, accurate answers around the clock.",
    roi: "Support capacity effectively doubled without new headcount.",
  },
  {
    id: "cs4",
    client: "Lumen Manufacturing",
    title: "Real-time visibility across the factory floor",
    industry: "Manufacturing",
    image: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&w=1200&q=80",
    challenge: "Disconnected machines and manual reporting meant no real-time view of production, causing costly downtime.",
    solution: "We built an IoT + ERP platform streaming machine data into live dashboards with predictive maintenance alerts.",
    technology: [".NET", "Azure IoT", "Power BI", "Databricks"],
    results: [
      { metric: "22%", label: "Less downtime" },
      { metric: "18%", label: "Higher OEE" },
      { metric: "Real-time", label: "Floor visibility" },
    ],
    impact: "Plant managers act on issues before they escalate, boosting throughput and reducing waste.",
    roi: "Downtime savings recovered the investment within the first year.",
  },
];

export const faqs = [
  { q: "What services does NeuroTriq TechLabs offer?", a: "We provide software development, web and mobile apps, cloud services, DevOps, AI solutions, data engineering, cyber security and managed IT services — end to end, from strategy to support." },
  { q: "How do you price your projects?", a: "We offer transparent, milestone-based pricing. After a short discovery, we share a detailed estimate with scope and timeline. We also support dedicated-team and time-and-materials models." },
  { q: "How long does a typical project take?", a: "MVPs typically launch in 6–12 weeks, while larger platforms run in phased releases. We prioritize shipping value early and iterating with you." },
  { q: "Do you work with startups as well as enterprises?", a: "Yes. Our architectures scale from a first MVP to millions of users, and we tailor our process to your stage, budget and goals." },
  { q: "Which industries do you specialize in?", a: "We have deep experience in healthcare, finance, retail, manufacturing, education, logistics, telecom, real estate and startups." },
  { q: "How do you ensure the security of our data?", a: "Security is built into every stage — secure coding, VAPT, identity management, encryption and compliance-aligned processes. We can align to HIPAA, SOC 2 and GDPR requirements." },
  { q: "Can you take over an existing project or codebase?", a: "Absolutely. We regularly audit, stabilize and modernize existing systems, then take ownership of ongoing development and support." },
  { q: "What is your development process?", a: "We follow a proven seven-stage process: Discovery, Planning, Design, Development, Testing, Deployment and Support — all with weekly demos and full transparency." },
  { q: "Do you provide ongoing support and maintenance?", a: "Yes. We offer 24×7 monitoring, SLAs, AMC and dedicated support so your systems stay reliable and secure long after launch." },
  { q: "Which technologies do you work with?", a: "React, Next.js, Node.js, Python, Java, .NET, Flutter, React Native, Docker, Kubernetes, AWS, Azure, GCP, Snowflake, Databricks and leading AI models like OpenAI, Claude and Gemini." },
  { q: "Can you build AI features into our product?", a: "Yes. We build generative AI assistants, RAG systems, chatbots and automation with responsible guardrails, integrating OpenAI, Claude and Gemini." },
  { q: "Do you offer cloud migration services?", a: "We plan and execute secure, low-risk migrations to AWS, Azure or GCP, with cost optimization and modern architecture as part of the move." },
  { q: "How do we get started?", a: "Book a free consultation through our contact form. We'll discuss your goals and follow up within one business day with next steps." },
  { q: "Do you sign NDAs and protect IP?", a: "Yes. We're happy to sign an NDA before discovery, and all IP and code developed belongs to you." },
  { q: "What makes NeuroTriq different?", a: "Senior talent, transparent delivery, security-first engineering and a genuine focus on business outcomes — not just shipping code." },
  { q: "Do you offer staff augmentation or dedicated teams?", a: "Yes. We can embed vetted engineers, designers and architects into your team, scaling up or down as your needs change." },
];

export const blog = [
  {
    slug: "generative-ai-in-the-enterprise",
    title: "Generative AI in the Enterprise: From Pilot to Production",
    category: "AI",
    date: "Nov 12, 2025",
    readTime: "8 min read",
    author: "Elena Petrova",
    image: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=1200&q=80",
    excerpt: "Most AI pilots stall before they deliver value. Here's the engineering discipline that gets generative AI safely into production.",
    content: [
      "Generative AI has moved from novelty to necessity, yet the gap between an impressive demo and a dependable production system remains vast. The difference is rarely the model — it's the engineering around it.",
      "Start with a narrow, high-value use case. Ground the model in your own data with retrieval-augmented generation (RAG), and treat prompts, evaluations and guardrails as first-class software artifacts.",
      "Observability is non-negotiable. Log inputs, outputs and human feedback so you can measure quality, catch regressions and continuously improve. Add human-in-the-loop handoff for edge cases.",
      "Finally, align with your compliance and security teams early. Responsible AI isn't a blocker — it's what lets you ship to real users with confidence.",
    ],
  },
  {
    slug: "cloud-cost-optimization-playbook",
    title: "The Cloud Cost Optimization Playbook for 2025",
    category: "Cloud",
    date: "Oct 28, 2025",
    readTime: "6 min read",
    author: "James Carter",
    image: "https://images.pexels.com/photos/17489163/pexels-photo-17489163.jpeg?auto=compress&cs=tinysrgb&w=1200",
    excerpt: "Cloud bills creep up quietly. A pragmatic FinOps approach can reclaim 30%+ without slowing your teams down.",
    content: [
      "Cloud spend rarely balloons overnight — it drifts. Idle resources, over-provisioned instances and forgotten environments quietly add up month after month.",
      "A FinOps culture makes cost a shared, visible metric. Tag everything, set budgets and alerts, and give engineers real-time visibility into the cost of their choices.",
      "Right-sizing, autoscaling, spot instances and committed-use discounts are the biggest levers. Combined, they routinely reclaim 30% or more with zero impact on reliability.",
      "The goal isn't to spend less — it's to spend deliberately, so every dollar maps to business value.",
    ],
  },
  {
    slug: "zero-trust-security-basics",
    title: "Zero Trust Security: A Practical Starting Point",
    category: "Cyber Security",
    date: "Oct 9, 2025",
    readTime: "7 min read",
    author: "Aisha Khan",
    image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=1200&q=80",
    excerpt: "Perimeter security is no longer enough. Here's how to adopt zero trust without grinding your organization to a halt.",
    content: [
      "The old model — trust everything inside the network — collapses in a world of remote work, SaaS and cloud. Zero trust assumes breach and verifies every request.",
      "Start with strong identity: enforce MFA, least-privilege access and short-lived credentials. Identity is the new perimeter.",
      "Segment your network and workloads so a single compromise can't spread. Encrypt data in transit and at rest, and monitor continuously.",
      "Adopt incrementally. Pick one critical system, apply zero-trust principles, learn, and expand — momentum beats a big-bang rollout.",
    ],
  },
  {
    slug: "microservices-vs-monolith",
    title: "Microservices vs. Monolith: Choosing What's Right",
    category: "Software Development",
    date: "Sep 22, 2025",
    readTime: "9 min read",
    author: "Rohan Mehta",
    image: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=1200&q=80",
    excerpt: "Microservices aren't automatically better. The right architecture depends on your team, scale and goals.",
    content: [
      "Microservices promise independent deployability and scale, but they add real operational complexity. For many teams, a well-structured monolith is the faster, cheaper path.",
      "Start with a modular monolith. Keep clear boundaries so you can extract services later — only when a specific need (scale, team autonomy, isolation) justifies it.",
      "When you do split, invest in the platform: CI/CD, observability, service discovery and contracts. Without it, microservices become a distributed monolith.",
      "Architecture is a means, not a goal. Choose the simplest design that meets today's needs and can evolve with tomorrow's.",
    ],
  },
  {
    slug: "shipping-faster-with-devops",
    title: "Shipping Faster: DevOps Practices That Actually Move the Needle",
    category: "DevOps",
    date: "Sep 3, 2025",
    readTime: "6 min read",
    author: "James Carter",
    image: "https://images.unsplash.com/photo-1607799279861-4dd421887fb3?auto=format&fit=crop&w=1200&q=80",
    excerpt: "DevOps isn't a tool you buy — it's a set of habits. These are the ones with the highest return.",
    content: [
      "Elite teams deploy on demand, recover fast and rarely break production. The secret isn't heroics — it's automation and small batches.",
      "Automate your pipeline end to end: build, test, security scan and deploy. Make releases boring and reversible.",
      "Measure the four DORA metrics — deployment frequency, lead time, change failure rate and time to restore. What you measure, you improve.",
      "Invest in developer experience. Fast feedback loops and reliable tooling are the highest-leverage investments a platform team can make.",
    ],
  },
  {
    slug: "modern-data-stack-explained",
    title: "The Modern Data Stack, Explained for Business Leaders",
    category: "Data",
    date: "Aug 15, 2025",
    readTime: "7 min read",
    author: "Elena Petrova",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80",
    excerpt: "Snowflake, Databricks, ELT, BI — what it all means and how to turn data into decisions.",
    content: [
      "The modern data stack replaces brittle, hand-built pipelines with managed, cloud-native tools that scale automatically and lower total cost of ownership.",
      "Data lands in a warehouse or lakehouse like Snowflake or Databricks, is transformed with ELT, and surfaced through BI tools like Power BI for decision-makers.",
      "Governance matters: define ownership, quality checks and access controls so leaders can trust the numbers they act on.",
      "Done well, the payoff is a single source of truth — and faster, more confident decisions across the business.",
    ],
  },
];

export const blogCategories = ["All", "AI", "Cloud", "Cyber Security", "DevOps", "Software Development", "Data"];

export const jobs = [
  { id: "j1", title: "Senior Full-Stack Engineer", team: "Engineering", type: "Full-time", location: "Remote / San Francisco", desc: "Build scalable web platforms across React, Node.js and cloud infrastructure for enterprise clients." },
  { id: "j2", title: "AI / ML Engineer", team: "AI Solutions", type: "Full-time", location: "Remote", desc: "Design and ship RAG systems and LLM integrations with OpenAI, Claude and Gemini in production." },
  { id: "j3", title: "DevOps Engineer", team: "Platform", type: "Full-time", location: "Remote / Austin", desc: "Own CI/CD, Kubernetes and infrastructure-as-code to make releases fast and reliable." },
  { id: "j4", title: "Product Designer (UX/UI)", team: "Design", type: "Full-time", location: "Remote", desc: "Craft premium, accessible experiences for complex enterprise products end to end." },
  { id: "j5", title: "Cloud Solutions Architect", team: "Cloud", type: "Full-time", location: "Hybrid / New York", desc: "Lead cloud migrations and architecture across AWS, Azure and GCP with security and FinOps in mind." },
  { id: "j6", title: "Cyber Security Analyst", team: "Security", type: "Full-time", location: "Remote", desc: "Run assessments, VAPT and compliance programs that reduce risk for our clients." },
];

export const benefits = [
  { icon: "Globe2", title: "Remote-first", text: "Work from anywhere with flexible hours and async collaboration." },
  { icon: "GraduationCap", title: "Learning budget", text: "Annual stipend for courses, certifications and conferences." },
  { icon: "HeartPulse", title: "Health & wellness", text: "Comprehensive health coverage and wellness support." },
  { icon: "Plane", title: "Generous PTO", text: "Recharge with paid time off and company-wide breaks." },
  { icon: "TrendingUp", title: "Real growth", text: "Clear career ladders, mentorship and meaningful ownership." },
  { icon: "Users", title: "Great team", text: "Senior, kind, curious people who care about craft." },
];

export const hiringSteps = [
  { step: "01", title: "Application", text: "Send us your resume and a note about what you'd love to build." },
  { step: "02", title: "Intro call", text: "A friendly conversation about your experience and goals." },
  { step: "03", title: "Technical round", text: "A practical, real-world exercise — no trick questions." },
  { step: "04", title: "Meet the team", text: "Get to know your future teammates and how we work." },
  { step: "05", title: "Offer", text: "We move fast and make competitive, transparent offers." },
];

export const serviceOptions = services.map((s) => s.title);
export const budgetOptions = ["< $10k", "$10k – $25k", "$25k – $50k", "$50k – $100k", "$100k+"];
